// Ported from monorepo corpus/text/[id]/page.tsx — the authenticated body
// read. The placeholder vocabulary framing is preserved from the source: a
// visitor without a vocabulary profile sees total-count-only analysis, and
// the personalized numbers arrive when the reader profile wiring lands.
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router";
import type { ReadingCandidate } from "@bindery/shared/corpus/selection";
import type { TextDetail } from "@bindery/shared/corpus/text";
import { getText } from "../lib/api";
import { useTokenSession } from "@bindery/auth-consumer/react";
import { SessionGate } from "@bindery/ui/components/session/session-gate";
import { signIn } from "../lib/auth";
import { VocabularyAnalysis } from "../components/vocabulary-analysis";

type TextState =
  | { kind: "loading" }
  | { kind: "not_found" }
  | { kind: "error" }
  | { kind: "ready"; text: TextDetail };

function placeholderCandidate(text: TextDetail): ReadingCandidate {
  const totalWordCount = text.totalWordCount;
  return {
    id: text.id,
    title: text.title,
    totalWordCount,
    knownWordCount: 0,
    unknownWordCount: totalWordCount,
    knownPercent: 0,
    challengeScore: 0,
    source: text.source,
    publication: text.publicationTitle
      ? {
          id: "",
          title: text.publicationTitle,
          coverImage: null,
          author: text.authorName,
        }
      : null,
  };
}

export function TextDetailPage() {
  const { id } = useParams<{ id: string }>();
  const tokenState = useTokenSession();
  const [state, setState] = useState<TextState>({ kind: "loading" });

  useEffect(() => {
    if (tokenState.kind !== "ready" || !id) return;
    let cancelled = false;
    void getText(tokenState.token, id).then((result) => {
      if (cancelled) return;
      setState(
        result.kind === "ok"
          ? { kind: "ready", text: result.data }
          : result.kind === "not_found"
            ? { kind: "not_found" }
            : { kind: "error" },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [tokenState, id]);

  return (
    <main className="mx-auto max-w-3xl px-4 py-8">
      <nav className="mb-6">
        <Link
          to="/recommend"
          className="text-muted-foreground hover:text-foreground text-sm hover:underline"
        >
          &larr; Back to recommendations
        </Link>
      </nav>

      <SessionGate
        status={tokenState.kind}
        onSignIn={() => signIn()}
        signedOutMessage="Texts are available to signed-in readers only."
        loading={<FetchingText />}
      >
        {state.kind === "loading" ? (
          <FetchingText />
        ) : state.kind === "not_found" ? (
          <div className="py-16 text-center">
            <h1 className="font-serif text-2xl font-bold">Text not found</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              Nothing in the corpus has that id.
            </p>
          </div>
        ) : state.kind === "error" ? (
          <p className="text-muted-foreground text-sm">
            The corpus is unavailable right now. Try again shortly.
          </p>
        ) : (
          <TextArticle text={state.text} />
        )}
      </SessionGate>
    </main>
  );
}

function FetchingText() {
  return <p className="text-muted-foreground text-sm">Fetching the text…</p>;
}

function TextArticle({ text }: { text: TextDetail }) {
  return (
    <article className="space-y-6">
      <header>
        <h1 className="font-serif text-4xl font-bold tracking-tight">
          {text.title ?? "Untitled"}
        </h1>
        {text.authorName && (
          <p className="text-muted-foreground mt-1 font-serif text-lg">
            {text.authorName}
          </p>
        )}
        {text.publicationTitle && (
          <p className="catalog-label mt-1.5">{text.publicationTitle}</p>
        )}
      </header>

      <div className="bg-muted/40 rounded-lg border p-4">
        <h2 className="catalog-label mb-3">Vocabulary analysis</h2>
        <VocabularyAnalysis candidate={placeholderCandidate(text)} />
        <p className="text-muted-foreground/70 mt-2 text-xs">
          Sign in with your reader profile to see personalized vocabulary
          analysis.
        </p>
      </div>

      <div className="font-serif text-[1.0625rem] leading-relaxed whitespace-pre-wrap">
        {text.text}
      </div>

      {text.source && (
        <footer className="catalog-rule pt-4">
          <p className="text-muted-foreground text-sm">Source: {text.source}</p>
        </footer>
      )}
    </article>
  );
}
